
import React, { useState } from "react";
import { Reagent } from "@/types/reagent.types";
import { deleteReagent } from "@/services/myReagents";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, Edit, Eye, Calendar, Package } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatDate } from "@/utils/dateUtils";
import LoadingSpinner from "@/components/common/LoadingSpinner";

interface UserReagentsTableProps {
  reagents: Reagent[];
  isLoading: boolean;
  onReagentDeleted: () => void;
}

const UserReagentsTable: React.FC<UserReagentsTableProps> = ({ 
  reagents, 
  isLoading, 
  onReagentDeleted 
}) => {
  const navigate = useNavigate();
  const [reagentToDelete, setReagentToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const handleDeleteReagent = async () => {
    if (!reagentToDelete) return;
    
    setIsDeleting(true);
    const success = await deleteReagent(reagentToDelete);
    setIsDeleting(false);
    
    if (success) {
      setReagentToDelete(null);
      onReagentDeleted();
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }
  
  if (reagents.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground mb-4">
            You haven't uploaded any reagents yet.
          </p>
          <Button onClick={() => navigate("/reagents/new")}>
            Upload Your First Reagent
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Your Reagents ({reagents.length})</h3>
        <Button onClick={() => navigate("/reagents/new")}>
          Upload New Reagent
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reagents.map((reagent) => (
          <Card key={reagent.id} className="overflow-hidden">
            <div className="aspect-video bg-muted relative">
              {(reagent.imageUrl || reagent.image_url) ? (
                <img 
                  src={reagent.imageUrl || reagent.image_url} 
                  alt={reagent.name} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-secondary/20">
                  <p className="text-muted-foreground">No image</p>
                </div>
              )}
              <div className="absolute top-2 right-2">
                {reagent.available ? (
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    Available
                  </span>
                ) : (
                  <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
                    Unavailable
                  </span>
                )}
              </div>
            </div>
            <CardContent className="p-4">
              <h4 className="font-semibold truncate">{reagent.name}</h4>
              <p className="text-sm text-muted-foreground mb-2">
                {reagent.category || (reagent.categories && reagent.categories.name) || "Uncategorized"}
              </p>
              
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm">
                  <span className="font-medium">Amount:</span>{" "}
                  {reagent.amount ? `${reagent.amount}${reagent.amount_unit || ""}` : "Not specified"}
                </p>
                
                {(reagent.numberOfPackages || reagent.number_of_packages) && (
                  <p className="text-sm flex items-center">
                    <Package className="h-4 w-4 mr-1" />
                    <span>{reagent.numberOfPackages || reagent.number_of_packages} pkg</span>
                  </p>
                )}
              </div>
              
              {(reagent.expiryDate || reagent.expiry_date) && (
                <p className="text-sm flex items-center mb-2">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>Expires: {formatDate(reagent.expiryDate || reagent.expiry_date)}</span>
                </p>
              )}
              
              <p className="text-xs text-muted-foreground mb-4">
                Uploaded on {formatDate(reagent.dateAdded || reagent.created_at || "")}
              </p>
              
              <div className="flex justify-between">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate(`/reagents/${reagent.id}`)}
                >
                  <Eye className="h-4 w-4 mr-1" /> View
                </Button>
                <div className="space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/reagents/edit/${reagent.id}`)}
                  >
                    <Edit className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => setReagentToDelete(reagent.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <AlertDialog open={reagentToDelete !== null} onOpenChange={(open) => !open && setReagentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this reagent?</AlertDialogTitle>
            <AlertDialogDescription>
              This reagent will be removed from public view and moved to your "Removed Reagents" tab. 
              You can make it available again later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteReagent}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <LoadingSpinner size="sm" /> : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserReagentsTable;
